import { Button } from "@workspace/web-ui/components/button";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@workspace/web-ui/components/form";

import { Allergy } from "~/features/dashboard/pages/medicals/types";

import { Input } from "@workspace/web-ui/components/input";
import { useAllergy } from "~/features/dashboard/pages/medicals/hooks/use-allergy";
import { CustomSelect } from "./custom-select";
import { useParams } from "@tanstack/react-router";

interface AllergyFormProps {
    allergy?: Allergy | null;
    onClose?: () => void;
}

const medicationOptions = [
    {
        value: "Mild",
        label: "Mild",
        description: "Causes minor symptoms like slight rash, sneezing, or itching",
    }, {
        value: "Moderate",
        label: "Moderate",
        description: "Symptoms are uncomfortable but not life-threatening (e.g., swelling, hives, nausea)",
    },
    {
        value: "Severe",
        label: "Severe",
        description: "Serious reactions such as difficulty breathing, chest tightness.",
    },
    {
        value: "Unknown",
        label: "Unknown",
        description: "Not sure how severe the reaction is or was",
    },
]

export const AllergyForm = ({ allergy, onClose }: AllergyFormProps) => {
    const { program } = useParams({ strict: false })
    const { form, onSubmit, isLoading } = useAllergy({ allergy, onClose, program: program === 'weight-loss' ? 'weight_loss' : 'urgent_care' });

    return (
        <Form {...form}>
            <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-4"
                aria-label={allergy ? "Update Allergy Form" : "Add Allergy Form"}
            >
                <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel htmlFor="name">What are you allergic to?</FormLabel>
                            <FormControl>
                                <Input
                                    id="name"
                                    className="w-full"
                                    placeholder="E.g. Pollen"
                                    aria-label="Name"
                                    aria-required="true"
                                    {...field}
                                />
                            </FormControl>
                            <FormMessage role="alert" />
                        </FormItem>
                    )}
                />
                <FormField
                    control={form.control}
                    name="severity"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Status</FormLabel>
                            <FormControl>
                                <CustomSelect
                                    options={medicationOptions}
                                    value={field.value}
                                    onChange={field.onChange}
                                    placeholder="Select"
                                />
                            </FormControl>
                            <FormMessage role="alert" />
                        </FormItem>
                    )}
                />

                <FormField
                    control={form.control}
                    name="symptoms"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel htmlFor="symptoms">What are the symptoms</FormLabel>
                            <FormControl>
                                <Input
                                    id="symptoms"
                                    className="w-full"
                                    placeholder="E.g. Rashes"
                                    aria-label="symptoms"
                                    aria-required="true"
                                    {...field}
                                />
                            </FormControl>
                            <FormMessage role="alert" />
                        </FormItem>
                    )}
                />

                <div className="flex items-center w-full border-t border-[#DCDCDE] pt-4 justify-end gap-2">
                    <Button
                        className="transition-colors rounded-[8px] bg-[#4272DD] w-full duration-200 "
                        type="submit"
                        disabled={isLoading}
                        aria-busy={isLoading}
                    >
                        {isLoading ? allergy ? "Updating..." : "Saving..." : "Save"}
                    </Button>
                </div>
            </form>
        </Form>
    );
};
